import { useCallback, useMemo, useState } from "react";
import { compareChatPath, makeCompareQuestion, selectionProblem } from "@/lib/compareSelection";

// Iter 63 — page-local "compare selection mode": pick two objects, then open chat with the question prepared.
export function useCompareSelection() {
  const [active, setActive] = useState(false);
  const [first, setFirst] = useState(null);
  const [second, setSecond] = useState(null);
  const [notice, setNotice] = useState(null);

  const clear = useCallback(() => {
    setFirst(null);
    setSecond(null);
    setNotice(null);
  }, []);

  const toggleActive = useCallback(() => {
    setActive((a) => {
      if (a) clear();
      return !a;
    });
  }, [clear]);

  const select = useCallback((obj) => {
    if (!obj) return;
    setNotice(null);
    if (first && first.key === obj.key) {
      setFirst(second);
      setSecond(null);
      return;
    }
    if (second && second.key === obj.key) {
      setSecond(null);
      return;
    }
    const problem = selectionProblem(first, obj);
    if (problem) {
      setNotice(problem);
      return;
    }
    if (!first) setFirst(obj);
    else setSecond(obj); // fills the second slot, or replaces it when both are taken
  }, [first, second]);

  const isSelected = useCallback(
    (obj) => Boolean(obj && ((first && first.key === obj.key) || (second && second.key === obj.key))),
    [first, second],
  );

  const question = useMemo(() => makeCompareQuestion(first, second), [first, second]);
  const path = useMemo(() => compareChatPath(first, second), [first, second]);

  return { active, toggleActive, first, second, select, clear, isSelected, question, path, notice };
}

export default useCompareSelection;
